import { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, Users, BarChart3, ArrowLeft, Mail, Loader2, Lock, Sparkles } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { LoginForm } from '../features/auth/components/LoginForm';
import { ForgotPasswordForm } from '../features/auth/components/ForgotPasswordForm';
import { AppLogo } from '../components/ui/AppLogo';
import { useBranding } from '../features/branding';
import { Button } from '../components/ui/button';
import { authService } from '../services/auth.service';
import type { LoginFormData } from '../features/auth/schemas/auth.schema';

type AuthView = 'login' | 'method-choice' | 'magic-link-sent' | 'forgot-password';

/**
 * Login Page
 *
 * Tela de autenticação do Atriva.
 * Design baseado em UI.pen (frame gCZQl - "00 - Login")
 *
 * Layout:
 * - Left Panel (720px): Branding com features e tagline
 * - Right Panel: Formulário de login ou recuperação de senha
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, forgotPassword, isLoading, error, clearError } = useAuthStore();
  const { branding } = useBranding();
  const tenantId = branding.tenantId ?? null;

  const [view, setView] = useState<AuthView>('login');
  const [emailForChoice, setEmailForChoice] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const [hasValidEmail, setHasValidEmail] = useState(false);
  const emailRef = useRef('');

  const from = (location.state as { from?: string })?.from || '/pipeline';

  const handleLogin = useCallback(async (data: LoginFormData) => {
    clearError();
    try {
      await login(data.email, data.password, data.tenantId ?? tenantId);
      
      // Redirect MASTER to /system, ORG_OWNER to /system/organizations/:orgId or /system/tenants, CRM roles to their routes
      const currentUser = useAuthStore.getState().user;
      if (currentUser?.role === 'master') {
        navigate('/system', { replace: true });
      } else if (currentUser?.role === 'org_owner') {
        const orgTarget = currentUser.organizationId
          ? `/system/organizations/${currentUser.organizationId}`
          : '/system/tenants';
        navigate(orgTarget, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch {
      // Error is handled by store
    }
  }, [login, navigate, from, clearError, tenantId]);

  const handleForgotPassword = useCallback(async (email: string, formTenantId?: string | null) => {
    clearError();
    await forgotPassword(email, formTenantId ?? tenantId);
  }, [forgotPassword, clearError, tenantId]);

  const switchToForgot = useCallback(() => {
    clearError();
    setView('forgot-password');
  }, [clearError]);

  const switchToLogin = useCallback(() => {
    clearError();
    setMagicLinkError(null);
    setView('login');
  }, [clearError]);

  const handleEmailContinue = useCallback((email: string) => {
    clearError();
    setEmailForChoice(email);
    setView('method-choice');
  }, [clearError]);

  const handleChoosePassword = useCallback(() => {
    clearError();
    setView('login');
    // emailForChoice will be available for LoginForm defaultValues if needed
  }, [clearError]);

  const handleChooseMagicLink = useCallback(async () => {
    setMagicLinkLoading(true);
    setMagicLinkError(null);
    try {
      await authService.requestMagicLink({
        email: emailForChoice,
        ...(tenantId ? { tenantId } : {}),
      });
      setView('magic-link-sent');
    } catch (err: unknown) {
      const error = err as {
        response?: { status?: number; data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      // 401 → mensagem genérica (defesa contra cross-tenant enumeration)
      const fallback =
        error.response?.status === 401
          ? 'Credenciais inválidas'
          : error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            'Erro ao enviar link mágico. Tente novamente.';
      setMagicLinkError(fallback);
    } finally {
      setMagicLinkLoading(false);
    }
  }, [emailForChoice, tenantId]);

  return (
    <div className="light flex h-screen" style={{ colorScheme: 'light' }}>
      {/* Left Panel - Branding */}
      <div className="hidden w-[720px] bg-brand-primary lg:flex flex-col justify-between p-16">
        {/* Logo and Tagline */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <AppLogo
              iconClassName="w-10 h-10 bg-white/10"
              showName
              nameClassName="text-white text-2xl"
            />
          </div>

          <p className="text-white text-3xl font-medium leading-[1.4] opacity-90">
            Gerencie seu pipeline de vendas
            <br />
            de forma inteligente
          </p>
        </div>

        {/* Features */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-semibold text-base">Pipeline Visual</span>
              <span className="text-white/70 text-sm">Acompanhe negociações em tempo real</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-semibold text-base">Gestão de Contatos</span>
              <span className="text-white/70 text-sm">Centralize informações dos clientes</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-semibold text-base">Relatórios Completos</span>
              <span className="text-white/70 text-sm">Analise métricas de vendas</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-white text-[13px] opacity-60">
          © {new Date().getFullYear()} {branding.organizationName}. Todos os direitos reservados.
        </p>
      </div>

      {/* Right Panel - Login / Forgot Password Form */}
      <div className="flex-1 bg-zinc-50 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-[420px] flex flex-col gap-7">
          {/* Header */}
          {(view === 'login' || view === 'method-choice') && (
            <div className="flex flex-col items-center gap-2">
              {/* Mobile logo */}
              <div className="mb-4 lg:hidden">
                <AppLogo
                  iconClassName="w-10 h-10 bg-brand-primary"
                  showName
                  nameClassName="text-zinc-900 dark:text-zinc-50 text-2xl"
                />
              </div>

              <h1 className="text-zinc-900 dark:text-zinc-50 text-3xl font-bold">
                {branding.loginTitle || 'Bem-vindo de volta!'}
              </h1>
              <p className="text-muted-foreground text-[15px]">
                {branding.loginSubtitle || 'Entre com suas credenciais para acessar'}
              </p>
            </div>
          )}

          {/* Form */}
          {view === 'login' && (
            <>
              <LoginForm
                onSubmit={handleLogin}
                onForgotPassword={switchToForgot}
                isLoading={isLoading}
                error={error}
                tenantId={tenantId}
                onEmailChange={(email) => {
                  emailRef.current = email;
                  setHasValidEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
                }}
              />

              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="text-muted-foreground text-sm">ou</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>

              <Button
                variant="outline"
                className="w-full gap-2"
                disabled={!hasValidEmail}
                onClick={() => {
                  handleEmailContinue(emailRef.current);
                }}
              >
                <Sparkles className="h-4 w-4" />
                Entrar com link mágico
              </Button>
            </>
          )}

          {view === 'method-choice' && (
            <div className="space-y-5">
              {emailForChoice ? (
                <p className="text-center text-sm text-muted-foreground">
                  Como deseja acessar com <span className="font-medium text-zinc-900 dark:text-zinc-50">{emailForChoice}</span>?
                </p>
              ) : (
                <p className="text-center text-sm text-destructive">
                  Por favor, informe seu e-mail antes de continuar.
                </p>
              )}

              {magicLinkError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {magicLinkError}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  variant="default"
                  className="w-full gap-2"
                  onClick={handleChoosePassword}
                >
                  <Lock className="h-4 w-4" />
                  Inserir senha
                </Button>

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleChooseMagicLink}
                  disabled={magicLinkLoading || !emailForChoice}
                >
                  {magicLinkLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Enviar link mágico
                    </>
                  )}
                </Button>
              </div>

              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={switchToLogin}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
            </div>
          )}

          {view === 'magic-link-sent' && (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-7 w-7 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Verifique seu e-mail</h3>
                <p className="text-sm text-muted-foreground">
                  Enviamos um link de acesso para{' '}
                  <span className="font-medium text-foreground">{emailForChoice}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  O link expira em 15 minutos.
                </p>
              </div>
              <div className="space-y-3">
                <Button variant="outline" className="w-full" onClick={switchToLogin}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar para o login
                </Button>
                <p className="text-xs text-muted-foreground">
                  Não recebeu?{' '}
                  <button
                    type="button"
                    onClick={handleChooseMagicLink}
                    disabled={magicLinkLoading}
                    className="text-primary hover:underline"
                  >
                    Reenviar link
                  </button>
                </p>
                {magicLinkError && (
                  <p className="text-xs text-destructive mt-1">
                    {magicLinkError}
                  </p>
                )}
              </div>
            </div>
          )}

          {view === 'forgot-password' && (
            <ForgotPasswordForm
              onSubmit={handleForgotPassword}
              onBackToLogin={switchToLogin}
              isLoading={isLoading}
              error={error}
              tenantId={tenantId}
            />
          )}

          {/* Demo Credentials (login view only, dev mode) */}
          {import.meta.env.DEV && view === 'login' && (
            <div className="p-4 bg-white border border-zinc-200 rounded-lg">
              <p className="text-muted-foreground text-xs font-medium mb-2">Credenciais de teste:</p>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>Admin: <code className="text-brand-primary">admin@atriva.com.br</code> / Admin@123</p>
                <p>Manager: <code className="text-brand-primary">manager@atriva.com.br</code> / Manager@123</p>
                <p>Sales: <code className="text-brand-primary">sales@atriva.com.br</code> / Sales@123</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
